import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import NewQuote from './NewQuote';

const NAV_HEIGHT = 72;

function Main() {
    const [quoteName, setQuoteName] = useState('');
    const [quoteEmail, setQuoteEmail] = useState('');
    const [quotePhone, setQuotePhone] = useState('');
    const [quoteMessage, setQuoteMessage] = useState('');
    const [quoteFiles, setQuoteFiles] = useState([]);
    const [quoteSending, setQuoteSending] = useState(false);
    const [quoteOk, setQuoteOk] = useState('');
    const [quoteErr, setQuoteErr] = useState('');

    useEffect(() => {
        fetch('/api/quoteRequest/home-view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        }).catch(() => { });
    }, []);

    const onQuoteSubmit = async (e) => {
        e.preventDefault();

        setQuoteOk('');
        setQuoteErr('');

        const name = quoteName.trim();
        const email = quoteEmail.trim();
        const phone = quotePhone.trim();
        const message = quoteMessage.trim();

        if (!name || !email || !message) {
            setQuoteErr('Please enter your name, email, and a message.');
            return;
        }

        const fd = new FormData();
        fd.append('name', name);
        fd.append('email', email);
        fd.append('phone', phone);
        fd.append('message', message);

        for (const f of quoteFiles) {
            fd.append('files', f);
        }

        setQuoteSending(true);

        try {
            const resp = await fetch('/api/quoteRequest', {
                method: 'POST',
                body: fd
            });

            const data = await resp.json().catch(() => ({}));

            if (!resp.ok || !data.ok) {
                throw new Error('send_failed');
            }

            setQuoteOk('Message sent. We will get back to you soon!');
            setQuoteName('');
            setQuoteEmail('');
            setQuotePhone('');
            setQuoteMessage('');
            setQuoteFiles([]);
        } catch (err) {
            setQuoteErr('Failed to send. Please try again or call/text instead.');
        } finally {
            setQuoteSending(false);
        }
    };

    const sections = useMemo(() => ([
        { id: 'about', label: 'About Us' },
        { id: 'services', label: 'Services' },
        { id: 'pricing', label: 'Pricing' },
        { id: 'quote', label: 'Quote' },
        { id: 'contact', label: 'Contact' }
    ]), []);

    const [activeId, setActiveId] = useState('about');
    const [underline, setUnderline] = useState({ left: 0, top: 0, width: 0 });

    const navLinksRef = useRef(null);
    const navItemRefs = useRef({});

    const setNavItemRef = useCallback((id) => (el) => {
        if (el) navItemRefs.current[id] = el;
    }, []);

    const updateUnderline = useCallback((id) => {
        const container = navLinksRef.current;
        const item = navItemRefs.current[id]?.querySelector('.nav-link-label');
        if (!container || !item) return;

        const c = container.getBoundingClientRect();
        const r = item.getBoundingClientRect();

        const left = r.left - c.left;
        const isNarrow = window.innerWidth < 760;
        const offset = isNarrow ? 2 : 6;

        const top = (r.bottom - c.top) + offset;

        setUnderline({
            left,
            top,
            width: r.width
        });
    }, []);


    useEffect(() => {
        updateUnderline(activeId);
    }, [activeId, updateUnderline]);

    useEffect(() => {
        const onResize = () => updateUnderline(activeId);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [activeId, updateUnderline]);

    useEffect(() => {
        const ids = sections.map((s) => s.id);

        let raf = 0;

        const computeActive = () => {
            raf = 0;

            const isNarrow = window.innerWidth < 760;
            const frac = isNarrow ? 0.40 : 0.25;
            const y = NAV_HEIGHT + Math.min(240, Math.floor(window.innerHeight * frac));

            let bestId = ids[0];
            let bestTop = -Infinity;

            for (const id of ids) {
                const el = document.getElementById(id);
                if (!el) continue;

                const r = el.getBoundingClientRect();

                if (r.top <= y && r.bottom > y) {
                    bestId = id;
                    break;
                }

                if (r.top <= y && r.top > bestTop) {
                    bestTop = r.top;
                    bestId = id;
                }
            }

            setActiveId(bestId);
        };

        const onScrollOrResize = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(computeActive);
        };

        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize);

        computeActive();

        return () => {
            window.removeEventListener('scroll', onScrollOrResize);
            window.removeEventListener('resize', onScrollOrResize);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [sections]);

    const scrollToSection = useCallback((id) => {
        const el = document.getElementById(id);
        if (!el) return;

        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    return (
        <div className='app'>
            <header className='nav'>
                <div className='nav-inner'>
                    <div className='nav-left' onClick={() => scrollToSection('about')} role='button' tabIndex={0}>
                        <img className='nav-logo' src='/logo-final-min.png' alt='North Florida Home Repair & Maintenance' />
                    </div>

                    <nav className='nav-right' ref={navLinksRef} aria-label='Site'>
                        {sections.map((s) => (
                            <button
                                key={s.id}
                                ref={setNavItemRef(s.id)}
                                className={`nav-link ${activeId === s.id ? 'is-active' : ''}`}
                                onClick={() => scrollToSection(s.id)}
                                type='button'
                            >
                                <span className='nav-link-label'>{s.label}</span>
                            </button>
                        ))}
                        <div
                            className='nav-underline'
                            style={{
                                transform: `translate(${underline.left}px, ${underline.top}px)`,
                                width: `${underline.width}px`
                            }}
                        />
                    </nav>
                </div>
            </header>

            <main className='page'>
                <section id='about' className='section section-about'>
                    <div className='hero'>
                        <img
                            src='/jax_small.jpg'
                            alt='North Florida Home Repair & Maintenance'
                            className='hero-image'
                        />

                        <div className='hero-overlay'>
                            <div className='hero-text-box'>
                                <div className='hero-title-main'>North Florida</div>
                                <div className='hero-title-sub'>Home Repair & Maintenance</div>
                            </div>
                        </div>
                    </div>

                    <div className='about-content'>
                        <p>
                            We provide reliable residential repair and maintenance services for homeowners throughout North Florida, including <b>Nassau, Duval, and St. Johns counties.</b><br /><br />The goal is simple: handle small to mid-sized projects professionally, show up when promised, and complete the work the right way the first time.
                        </p>
                        <hr />
                        <p>
                            Homeowners often need dependable help for repairs that are too small for large contractors but too important to ignore. <em>North Florida Home Repair & Maintenance</em> fills that gap by offering skilled, detail-oriented service with an emphasis on long-term fixes rather than quick patches. Every project is approached with care, respect for the home, and attention to clean, finished results. Whether addressing a specific repair or working through a list of maintenance items, the focus is on making the process straightforward, transparent, and stress-free for the customer.
                        </p>
                    </div>
                </section>

                <section id='services' className='section section-services'>
                    <div className='services-inner'>
                        <div className='services-header'>
                            <img className='services-header-icon' src='/services.webp' alt='' />
                            <div className='services-header-title'>Services</div>
                            <img className='services-header-icon' src='/services.webp' alt='' />
                        </div>

                        <div className='services-row'>
                            <div className='services-media'>
                                <img src='/roof.webp' alt='Roofing' className='services-img' />
                            </div>
                            <div className='services-text'>
                                <div className='services-title'>Roof Repairs</div>
                                <div className='services-body'>
                                    <b>15+ years combined experience in shingle AND metal roof repairs</b>
                                    <ul>
                                        <li>Minor shingle repair and replacement</li>
                                        <li>Repair and replacement of damaged metal roof panels</li>
                                        <li>Flashing inspection and resealing around penetrations</li>
                                        <li>Pipe boot inspection and replacement</li>
                                        <li>Sealant repair around vents, skylights, and roof fixtures</li>
                                        <li>Fascia and drip edge repairs</li>
                                        <li>Soffit repairs & soffit vent installation or repair</li>
                                        <li>Roof ventilation cover and vent cap repair or replacement</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className='services-row services-row-reverse'>
                            <div className='services-media'>
                                <img src='/drywall.jpg' alt='Drywall' className='services-img' />
                            </div>
                            <div className='services-text'>
                                <div className='services-title'>Drywall & Interior</div>
                                <div className='services-body'>
                                    <ul>
                                        <li>Drywall hole repair and patching</li>
                                        <li>Water-damaged drywall removal and replacement</li>
                                        <li>Ceiling drywall repair and patching</li>
                                        <li>Crack repair and seam reinforcement</li>
                                        <li>Texture matching and blending</li>
                                        <li>Drywall finishing and sanding</li>
                                        <li>Paint preparation after repairs</li>
                                        <li>Interior wall and ceiling touch-up painting</li>
                                        <li>Small-area repainting following repairs</li>
                                        <li>Trim and baseboard painting</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className='services-row'>
                            <div className='services-media'>
                                <img src='/carpentry.webp' alt='Carpentry' className='services-img' />
                            </div>
                            <div className='services-text'>
                                <div className='services-title'>Carpentry</div>
                                <div className='services-body'>
                                    <ul>
                                        <li>Interior trim and molding installation and repair</li>
                                        <li>Door installation, adjustment, and repair</li>
                                        <li>Interior wall framing for small projects</li>
                                        <li>Closet shelving installation and customization</li>
                                        <li>Cabinet hardware installation and replacement</li>
                                        <li>Window trim repair and replacement</li>
                                        <li>Wood rot repair (non-structural)</li>
                                        <li>Exterior fascia and soffit repair</li>
                                        <li>Fence repair and gate adjustments</li>
                                        <li>Deck board replacement and minor deck repairs</li>
                                        <li>Garage shelving and storage solutions</li>
                                        <li>Minor framing repairs after water damage</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className='services-row services-row-reverse'>
                            <div className='services-media'>
                                <img src='/plumbing.webp' alt='Plumbing' className='services-img' />
                            </div>
                            <div className='services-text'>
                                <div className='services-title'>Plumbing</div>
                                <div className='services-body'>
                                    <ul>
                                        <li>Sink / faucet repair and replacement</li>
                                        <li>Toilet repair and replacement</li>
                                        <li>Garbage disposal installation and replacement</li>
                                        <li>Installation and replacement of inline water filters</li>
                                        <li>P-trap repair and replacement</li>
                                        <li>Shutoff valve replacement and installation</li>
                                        <li>Showerhead and handheld sprayer installation</li>
                                        <li>Bathroom and kitchen fixture resealing and caulking</li>
                                        <li>Supply line replacement for sinks and toilets</li>
                                        <li>Water line connections for appliances (washer, refrigerator, dishwasher)</li>
                                        <li>Minor plumbing adjustments during renovations or repairs</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className='services-row'>
                            <div className='services-media'>
                                <img src='/electrical.jpg' alt='Electrical' className='services-img' />
                            </div>
                            <div className='services-text'>
                                <div className='services-title'>Electrical</div>
                                <div className='services-body'>
                                    <ul>
                                        <li>Light fixture installation and replacement</li>
                                        <li>Ceiling fan installation and replacement</li>
                                        <li>Switch and outlet replacement, dimmer switch installation</li>
                                        <li>Smart switch and smart device installation</li>
                                        <li>Exterior light fixture replacement</li>
                                        <li>Doorbell repair and replacement</li>
                                        <li>Smoke and carbon monoxide detector installation and replacement</li>
                                        <li>Bathroom exhaust fan replacement</li>
                                        <li>Under-cabinet lighting installation</li>
                                        <li>Garage and utility lighting upgrades</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className='services-row services-row-reverse'>
                            <div className='services-media'>
                                <img src='/tv.png' alt='tv' className='services-img' />
                            </div>
                            <div className='services-text'>
                                <div className='services-title'>Odds & Ends</div>
                                <div className='services-body'>
                                    <ul>
                                        <li>TV mounting and wall bracket installation</li>
                                        <li>Appliance installation and replacement</li>
                                        <li>Washer and dryer setup</li>
                                        <li>Dishwasher installation</li>
                                        <li>Blind and curtain rod installation</li>
                                        <li>Mirror and wall décor mounting</li>
                                        <li>Shelving and storage installation</li>
                                        <li>Furniture assembly</li>
                                        <li>Weatherstripping installation and replacement</li>
                                        <li>Smoke and carbon monoxide detector installation</li>
                                        <li>Minor home upgrades and fixture replacements</li>
                                        <li>Punch list and small repair completion</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section id='pricing' className='section section-pricing'>
                    <div className='pricing-inner'>
                        <div className='pricing-block'>
                            <div className='pricing-block-title'>Simple, Transparent Pricing</div>

                            <div className='pricing-block-body'>
                                <p>Most small handyman jobs fall between $150-$350, depending on scope, with a minimum service call of $100.</p>
                                <p>Clear quotes are provided before work begins so there are no surprises.</p>
                                <p>For larger projects or jobs involving multiple tasks, hourly pricing may apply.</p>
                                <p><b>Free estimates:</b> available based on a brief description and photos of the project.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section id='quote' className='section section-quote'>
                    <div className='quote-inner'>
                        <div className='quote-header'>
                            <div className='quote-header-title'>Request a Quote</div>
                        </div>

                        <form className='quote-form' onSubmit={onQuoteSubmit}>
                            <div className='quote-row'>
                                <label className='quote-label'>
                                    Name
                                    <input
                                        type='text'
                                        className='quote-input'
                                        placeholder='Your name'
                                        value={quoteName}
                                        onChange={(e) => setQuoteName(e.target.value)}
                                    />
                                </label>

                                <label className='quote-label'>
                                    Email
                                    <input
                                        type='email'
                                        className='quote-input'
                                        placeholder='you@email.com'
                                        value={quoteEmail}
                                        onChange={(e) => setQuoteEmail(e.target.value)}
                                    />
                                </label>
                            </div>

                            <div className='quote-row'>
                                <label className='quote-label'>
                                    Phone
                                    <input
                                        type='tel'
                                        className='quote-input'
                                        placeholder='(904) 555-1234'
                                        value={quotePhone}
                                        onChange={(e) => setQuotePhone(e.target.value)}
                                    />
                                </label>
                            </div>

                            <label className='quote-label'>
                                How can we help?
                                <textarea
                                    className='quote-textarea'
                                    rows='5'
                                    placeholder='Describe the work you need done...'
                                    value={quoteMessage}
                                    onChange={(e) => setQuoteMessage(e.target.value)}
                                />
                            </label>

                            <label className='quote-label'>
                                (Optional) upload any photos or files that might help explain what you're needing
                                <input
                                    type='file'
                                    className='quote-file'
                                    multiple
                                    onChange={(e) => setQuoteFiles(Array.from(e.target.files || []))}
                                />
                            </label>

                            {quoteErr ? <div className='quote-msg quote-msg-err'>{quoteErr}</div> : null}
                            {quoteOk ? <div className='quote-msg quote-msg-ok'>{quoteOk}</div> : null}

                            <button type='submit' className='quote-submit' disabled={quoteSending}>
                                {quoteSending ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>
                </section>

                <section id='contact' className='section section-contact'>
                    <div className='contact-inner'>
                        <div className='contact-header'>
                            <div className='contact-title'>Contact Us</div>
                        </div>

                        <div className='contact-content'>
                            <div className='contact-photo'>
                                <img src='/profile.jpg' alt='Profile' className='contact-img' />
                            </div>

                            <div className='contact-details'>
                                <div className='contact-name'>Daniel Holida</div>
                                Intake & Lead Technician

                                <div className='contact-line'>
                                    <strong>Text or call:</strong> (904) 386-2663
                                    <br />
                                    <strong>Email:</strong> daniel@northfloridarepair.com
                                </div>

                                <div className='contact-hours'>
                                    <div className='contact-hours-title'>Operating Hours</div>
                                    <div>Monday-Friday: 7:00 AM - 6:00 PM</div>
                                    <div>Saturday: 9:00 AM - 5:00 PM</div>
                                    <div>Sunday: 11:00 AM - 5:00 PM</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}

const quoteFiles = require.context('./quotes', false, /\.js$/);
const quoteRoutes = quoteFiles.keys().map(key => {
    const id = key.replace('./', '').replace('.js', '');
    const Component = quoteFiles(key).default;
    return { id, Component };
});

function App() {
    return (
        <Routes>
            <Route path="/" element={<Main />} />
            <Route path="/new" element={<NewQuote />} />
            {quoteRoutes.map(({ id, Component }) => (
                <Route key={id} path={`/${id}`} element={<Component />} />
            ))}
        </Routes>
    );
}

export default App;
